import React, {useEffect, useState} from 'react';
import {ErrorMessage, Scrollbar} from '@priority-software/priority-style-react';
import {isRTL} from '../../../Localization';
import commonStyles from '../styles/list.module.scss';
import {DraggableList} from '../../../components/commonOldtoNew';
import {DraggableItem} from './DraggableItem';
import {ItemType, ListProps} from '../types';
import {getTabIndexByDroppableId, moveBetweenLists, reorder, reorderChildList} from '../utils/dnd';
import {TabViewItem} from './TabViewItem';
import {deserializeTabData, isInvalidDesign, serializeTabData} from '../utils/tab';
import {priority} from '@priority-software/priority-proto-api/lib';
import texts from '../texts';

export const TabViewList = ({
    data,
    updateData,
    pic,
    setPic,
    setSelectedItems,
    selectedItems,
    foundItemName,
    viewItemName,
    activeTab,
    setActiveTab,
    moveItemBetweenTabs,
    packages,
    setPackages
}: ListProps) => {
    const [serializedData, setSerializedData] = useState(serializeTabData(data));
    const [expandedTabs, setExpandedTabs] = useState(Array(data.length).fill(false));
    const [showInvalidToast, setShowInvalidToast] = useState(false);
    useEffect(() => {
        setSerializedData(serializeTabData(data));
        const showInvalidToast = isInvalidDesign(
            data as priority.netitems.form.RowViewTabsDesign[]
        );
        if (showInvalidToast) {
            setShowInvalidToast(true);
            setTimeout(() => {
                setShowInvalidToast(false);
            }, 10000);
        }
    }, [data]);

    useEffect(() => {
        if (foundItemName) {
            let foundTabIndex = null;
            for (let i = 0; i < serializedData.length; i++) {
                const found = serializedData[i].ColumnsDesign.find(
                    column =>
                        column.Name === foundItemName ||
                        (column.merged && column.merged.Name === foundItemName)
                );
                if (found) {
                    foundTabIndex = i;
                    break;
                }
            }
            if (foundTabIndex !== null && !expandedTabs[foundTabIndex]) {
                toggleTabExpended(foundTabIndex);
            }
        }
    }, [foundItemName]);

    const toggleTabExpended = (index: number) => {
        const clone = Array.from(expandedTabs);

        clone[index] = clone[index] ? undefined : true;
        if (clone[index]) {
            setActiveTab(index);
        } else if (activeTab === index) {
            setActiveTab(null);
        }
        setExpandedTabs(clone);
    };

    const updateTabData = data => {
        const deserializedRes = deserializeTabData(data);
        updateData(deserializedRes);
    };

    const setTabItems = result => {
        if (
            result.destination.droppableId === result.source.droppableId &&
            result.destination.index === result.source.index
        ) {
            return;
        }
        if (result.destination.droppableId === result.source.droppableId) {
            if (result.destination.droppableId === 'droppable') {
                const items = reorder(
                    serializedData,
                    result.source.index,
                    result.destination.index
                );
                updateTabData(items);
                if (expandedTabs[result.source.index] !== expandedTabs[result.destination.index]) {
                    expandedTabs[result.source.index] = !expandedTabs[result.source.index];
                    expandedTabs[result.destination.index] =
                        !expandedTabs[result.destination.index];
                }
            } else {
                const droppableIdArr = result.destination.droppableId.split('-');
                const droppableIndex = Number(droppableIdArr[droppableIdArr.length - 1]);
                reorderTab(result.source.index, result.destination.index, droppableIndex);
            }
        } else {
            const res = moveBetweenLists(serializedData, result);
            const destIndex = getTabIndexByDroppableId(result.destination.droppableId);
            setActiveTab(destIndex);
            updateTabData(res);
        }
    };

    const reorderTab = (startIndex: number, endIndex: number, tabIndex: number) => {
        const res = reorderChildList(serializedData, startIndex, endIndex, tabIndex);
        updateTabData(res);
    };

    return (
        <>
            <Scrollbar componentClasses={commonStyles.scrollbar} rtl={isRTL()}>
                <DraggableList
                    componentClasses={commonStyles.list}
                    data={serializedData}
                    onDragEnd={setTabItems}
                    renderItem={({item, index, ...dragProps}) => (
                        <DraggableItem
                            {...dragProps}
                            // TODO: think what to use as a key
                            // when we get current design
                            // and create a common type for it
                            key={(item as ItemType)?.Title}
                        >
                            <TabViewItem
                                index={index}
                                tab={item}
                                pic={pic}
                                setPic={setPic}
                                data={serializedData}
                                setSelectedItems={setSelectedItems}
                                selectedItems={selectedItems}
                                isExpended={expandedTabs[index]}
                                toggleTabExpended={toggleTabExpended}
                                foundItemName={foundItemName}
                                viewItemName={viewItemName}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                moveItemBetweenTabs={moveItemBetweenTabs}
                                onUpdate={() => updateTabData(serializedData)}
                                reorderTab={reorderTab}
                                packages={packages}
                                setPackages={setPackages}
                                {...dragProps}
                            />
                        </DraggableItem>
                    )}
                />
            </Scrollbar>
            {showInvalidToast && (
                <ErrorMessage
                    message={texts.invalidToast}
                    onClose={() => setShowInvalidToast(false)}
                    componentClasses={commonStyles.toast}
                    animated
                />
            )}
        </>
    );
};
